import { Spin, Modal } from 'antd';
import React, { memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import PageSizeButton from '../../../../components/PageSizeButton/PageSizeButton';
import pageSizes from '../../../../components/PageSizeButton/pageSizes';
import Pagination from '../../../../components/Pagination/Pagination';
import usePagination from '../../../../hooks/usePagination';
import useParam from '../../../../hooks/useParam';
import ContractorModal from '../../components/modals/contractorModal/ContractorModal/ContractorModal';
import LayeredTable from '../../components/tables/layeredTable/LayeredTable/LayeredTable';
import queries from '../../queries/queries';
import useCounterparties from '../../queries/useCounterparties';
import useCounterpartiesTypes from '../../queries/useCounterpartiesTypes';
import useCountries from '../../queries/useCountries';
import deleteCounterparty from '../../requests/deleteCounterparty';
import properties from '../constants/properties';
import CounterpartiesExpandedRow from '../CounterpartiesExpandedRow/CounterpartiesExpandedRow';
import useSetRefreshFn from '../../../../hooks/useSetRefreshFn';
import styles from './Counterparties.module.scss';

const { confirm } = Modal;

const Counterparties = () => {
  useCountries();
  useCounterpartiesTypes();
  const userCanDelete = useSelector(
    (store) => store.authentication.userInfo.CanDeleteOffice
  );
  const userCanEdit = useSelector(
    (store) => store.authentication.userInfo.CanCreateOffice
  );
  const queryClient = useQueryClient();
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [editingCounterparty, setEditingCounterparty] = useState(null);
  const searchString = useParam('search', '');
  const { data: counterparties, isLoading } = useCounterparties(searchString);
  const [
    currentPageNumber,
    setCurrentPageNumber,
    maxPage,
    shownCounterparties,
    firstIndexOnPage,
  ] = usePagination(counterparties || [], pageSize);
  const onDeleteRow = useCallback(
    (counterparty) => {
      confirm({
        title: `Вы уверены, что хотите удалить контрагента ${
          counterparty.name || ''
        }?`,
        content: 'Это действие нельзя будет отменить',
        onOk: async () => {
          await deleteCounterparty(counterparty.id);
          queryClient.invalidateQueries(queries.counterparties);
        },
        onCancel() {},
      });
    },
    [queryClient]
  );

  useSetRefreshFn(() => {
    queryClient.invalidateQueries([queries.counterparties, searchString]);
  }, [searchString]);
  const onEditRow = useCallback((_isEditing, counterparty) => {
    setEditingCounterparty(counterparty);
  }, []);
  const onCloseEdit = useCallback(() => {
    setEditingCounterparty(null);
  }, []);

  return (
    <Spin spinning={isLoading}>
      <LayeredTable
        editable={userCanEdit}
        deletable={userCanDelete}
        data={shownCounterparties}
        rowDataPropertiesArray={properties}
        expandable={'end'}
        expandedRowContent={(counterparty) => (
          <CounterpartiesExpandedRow counterparty={counterparty} />
        )}
        onDeleteRow={onDeleteRow}
        onEditRow={onEditRow}
        expandButtonFlex="0.1"
      />
      <Pagination
        maxPage={maxPage}
        setPageNumber={setCurrentPageNumber}
        currentPageNumber={currentPageNumber}
        firstIndexOnPage={firstIndexOnPage}
        lastIndexOnPage={firstIndexOnPage + shownCounterparties.length}
        totalAmount={counterparties?.length}
      />
      <div className={styles.buttonRow}>
        <PageSizeButton pageSize={pageSize} setPageSize={setPageSize} />
      </div>
      {!!editingCounterparty && (
        <ContractorModal
          visible={!!editingCounterparty}
          onCancel={onCloseEdit}
          editingCounterparty={editingCounterparty}
        />
      )}
    </Spin>
  );
};

export default memo(Counterparties);
